import { DateTime } from "luxon";

export function formatVisitWhen(iso: string | null, fallbackIso: string): string {
    const d = DateTime.fromISO(iso ?? fallbackIso, { zone: "local" });
    return d.hasSame(DateTime.local(), "day") ? `Today ${d.toFormat("h:mm a")}` : d.toFormat("MMM d, h:mm a");
}
export const formatTime = (iso: string | null, fallbackIso: string) =>
    DateTime.fromISO(iso ?? fallbackIso, { zone: "local" }).toFormat("h:mm a");
/**
 * The time part of a server visit label ("Fluffy · 10:30 AM"), already in the clinic's timezone. Empty for a
 * label with no time (the Recycle Bin's "the kept visit" when the API could not read that visit).
 */
export const labelTime = (label: string) => (label.includes(" · ") ? (label.split(" · ").pop() ?? "") : "");

export const COPY = {
    pill: "Possible duplicate",
    popoverTitle: "Same patient, same day",
    linkedLine: (pims: string) => `Linked to ${pims}`,
    popoverBodyOtherLinked: "Merge them so your recording and documents sit on the linked visit and can export.",
    popoverBodyThisUnlinked: (pims: string) =>
        `This visit isn't linked to ${pims}, so it can't export. Merge it into the linked visit to fix that.`,
    popoverBodyNeitherLinked:
        "These look like the same appointment. Merge them to keep everything on one visit. You can undo it.",
    /** Opens the merge dialog, where the vet still chooses which visit to keep. */
    reviewMerge: "Review merge",
    pickAnother: "Merge with a different visit",
    notDuplicate: "Not a duplicate",
    exportTooltip: (pims: string, time: string) =>
        `This visit isn't linked to ${pims}. Merge it with the ${time} visit to export.`,
    /** Shown next to a disabled Export, where a tooltip never shows on touch. */
    exportMergeHint: (time: string) => `Can't export yet. Merge with the ${time} visit.`,
    dismissedToast: "Got it. We won't flag these two visits again.",
    /** Shown when "Not a duplicate" or its Undo fails. */
    dismissFailed: "Couldn't save that. Please try again.",
    undo: "Undo",
    menuMerge: "Merge with another visit",
    menuUnmerge: (time: string) => `Unmerge the ${time} visit`,
    menuUnmergeSub: (date: string) => `Available until ${date}`,
    title: "Merge visits",
    subtitle: "Combine two visits for the same appointment into one.",
    sectionWith: "Merge with",
    sectionKeep: "Keep",
    sectionMoves: (time: string) => `Moves into the ${time} visit`,
    change: "Change",
    pickerLabel: (patient: string, time: string) =>
        patient && time
            ? `Merge ${patient}'s ${time} visit with`
            : time
              ? `Merge the ${time} visit with`
              : "Merge this visit with",
    pickerPlaceholder: "Search patient or owner (last 7 days)",
    groupLikely: "Likely duplicates",
    groupRecent: "Recent visits",
    pickerEmpty: "No visits found in the last 7 days.",
    pickerFailed: "Couldn't load visits.",
    previewFailedTitle: "Couldn't load these visits",
    previewFailedBody: "Check your connection and try again.",
    retry: "Retry",
    pickFirst: "Pick a visit to see which one is kept and what moves.",
    thisVisit: "This visit",
    linkedTo: (pims: string) => `Linked to ${pims}`,
    suggested: "Suggested",
    reasonLinked: (pims: string) => `Suggested because it's linked to ${pims}, so documents can export after merging.`,
    reasonContent: "Suggested because it has the recordings.",
    reasonCurrent: "Neither visit is linked to your PIMS, so we suggest keeping the one you're on.",
    reasonCurrentBothLinked: (pims: string) =>
        `Both visits are linked to ${pims}, so we suggest keeping the one you're on.`,
    /** Keep option content line: "2 clips · No documents". */
    keepContents: (clips: number, docs: number) =>
        [
            clips === 0 ? "No clips" : `${clips} ${clips === 1 ? "clip" : "clips"}`,
            docs === 0 ? "No documents" : `${docs} ${docs === 1 ? "document" : "documents"}`,
        ].join(" · "),
    thisVisitSuffix: "(this visit)",
    differentPatient: (other: string, opened: string) =>
        `Different patient: this is ${other || "a visit with no patient name"}, not ${opened || "a visit with no patient name"}. Only merge if they're the same appointment.`,
    differentDay: (date: string) => `Different day: this visit is on ${date}.`,
    confirmSameAppointment: "Yes, this is the same appointment",
    keptWillShow: "After merging, the visit shows",
    changeDetails: "Change details",
    linkedDetails: (pims: string) => `Details come from ${pims} for linked visits.`,
    overridesTitle: "Details for the merged visit",
    overridesFromColumn: (time: string) => `From the ${time} visit`,
    overridesKeepColumn: (time: string) => `Keep the ${time} visit's`,
    overrideOption: (field: string, value: string, time: string) => `${field}: ${value}, from the ${time} visit`,
    noDoctor: "No doctor",
    noRoom: "No room",
    unknownDoctor: "Unknown doctor",
    unknownRoom: "Unknown room",
    noType: "No type",
    noLength: "No length",
    resetToSuggested: "Reset to suggested",
    done: "Done",
    changed: "CHANGED",
    sameTypeNote: (docType: string) =>
        `Both visits have a ${docType}. Both are kept, newest first. You can archive one afterwards.`,
    noLinkNote: "Neither visit is linked, so documents still can't export after merging.",
    footerNote: (time: string) =>
        `The ${time} visit will be empty afterwards and moves to the Recycle Bin. You can undo this for 7 days.`,
    cancel: "Cancel",
    mergeVisits: "Merge visits",
    /** visitTitle: the kept visit as its Keep option names it ("10:30 AM visit (this visit)"). */
    mergeInto: (visitTitle: string) => `Merge into the ${visitTitle}`,
    reason: {
        recording_active: "Recording in progress. You can merge once it's saved.",
        different_pims_patient: (pims: string) => `Linked to a different ${pims} patient`,
        history_visit: "Past PIMS visits can't be merged",
        already_archived: "This visit was deleted.",
        visit_changed: "This visit changed while you were merging. Try again.",
        same_visit: "Pick a different visit.",
        account_mismatch: "That visit isn't in this clinic.",
        unmerge_unavailable: "This merge can no longer be undone.",
        clip_processing: "A clip on one of these visits is still saving. Try again in a minute.",
        document_conflict: "Both visits have the same kind of document for the same date. Archive one, then try again.",
        document_generating: "A document is still being written for one of these visits. Try again when it finishes.",
        idempotency_key_reused: "These visits changed since you opened this. Close and try again.",
    },
    /** A block reason this build does not know (the API shipped a new one first). */
    reasonFallback: "These visits can't be merged right now.",
    blockedRecordingTitle: (time: string) => `A recording started on the ${time} visit`,
    blockedRecordingBody:
        "We can't merge while a recording is in progress, so nothing has changed. Try again once it's saved.",
    liveCheck: "Checking again every 10 seconds. Merge turns back on by itself.",
    failedTitle: "Couldn't merge these visits",
    failedBody:
        "Something went wrong on our side. Nothing was moved, and both visits are exactly as they were. Please try again.",
    /** A merge or unmerge the server says will fail the same way on every retry. */
    mergeNotAutomatic: "These visits can't be merged automatically. Contact support.",
    overridesNotAllowed: "This visit is now linked, so its details can't be changed. Close and merge again.",
    roomNotAllowed:
        "This visit is now linked or already has its own room, so Room can't be changed. Go back and merge without it.",
    back: "Back",
    busy: "Another change to these visits was in progress. Try again.",
    unmergeChanged: "This visit changed while you were unmerging. Try again.",
    unmergeNotAutomatic: "This merge can't be undone automatically. Contact support.",
    /** An unknown outcome (no answer, or a 5xx) that the status poll never resolved. */
    unconfirmedTitle: "We couldn't confirm the merge",
    unconfirmedBody: "Check the visit list before trying again.",
    tryAgain: "Try again",
    close: "Close",
    merging: "Merging visits…",
    mergingFooter: "This usually takes a few seconds. Please keep this open.",
    stillWorking: "Still working. You can close this; we'll finish in the background.",
    successTitle: "Visits merged",
    successBody: (patient: string, time: string) =>
        `Everything is now on ${patient ? `${patient}'s` : "the"} ${time} visit. The other visit is in the Recycle Bin.`,
    readyToExport: (docType: string, pims: string) => `${docType} can now be exported to ${pims}.`,
    /** Opens the kept visit with Export focused; it never exports by itself. */
    exportDoc: "Open to export",
    openVisit: "Open visit",
    undoMerge: "Undo merge",
    /** Shown when Undo after a merge fails. */
    undoFailed: "Couldn't undo the merge. Please try again.",
    /** An undo or unmerge whose outcome is unknown (no reply, or a 5xx other than MERGE_FAILED) and that the status check did not show as unmerged. */
    undoUnconfirmed: "We couldn't confirm the merge was undone. Check the visit list before trying again.",
    provenance: (time: string, who: string, when: string) => `Merged from the ${time} visit by ${who} · ${when}`,
    details: "Details",
    provenanceUnmergeUntil: (date: string) => `You can unmerge until ${date}`,
    mergedToast: "Visits merged.",
    mergedAudioUnconfirmed: (n: number) =>
        `Visits merged, but audio for ${n} ${n === 1 ? "clip" : "clips"} could not be confirmed. Contact support if it doesn't play.`,
    unmergedAudioUnconfirmed: (n: number) =>
        `Unmerged, but audio for ${n} ${n === 1 ? "clip" : "clips"} could not be confirmed. Contact support if it doesn't play.`,
    mergedInTag: "Merged in",
    /** Picker row meta: "1 clip · 2 docs". */
    contentCounts: (clips: number, docs: number) =>
        `${clips} ${clips === 1 ? "clip" : "clips"} · ${docs} ${docs === 1 ? "doc" : "docs"}`,
    /** The opened visit itself can't be merged right now: its reason, labelled as this visit's. */
    thisVisitBlocked: (reason: string) => `This visit: ${reason}`,
    /** Visit page opened before this visit was merged into another one. */
    visitMergedAway: "This visit was merged into another visit.",
    /** Move recording refused: the clip left this visit since the page loaded (a merge or unmerge, another tab). */
    moveRecordingClipMoved: "This recording is no longer on this visit. The visit has been reloaded.",
    /** Move recording refused: the visit picked was merged into another one. */
    moveRecordingVisitMerged: "That visit was merged into another visit. Pick the visit it was merged into.",
    /** A recording start refused because the visit was merged into another visit since the page loaded. */
    startRefusedMergedAway: "This visit was merged into another visit, so recording didn't start.",
    /** Recording widget: the visit being recorded on was merged into another visit. */
    recordingMergedAway:
        "This visit was merged into another one while you were recording. Keep going: your recording is safe and will save to this visit. When you're done, open the other visit and tap Unmerge to bring this one back.",
    /** "2 clips, 1 document, 1 file". Zero counts are left out. */
    movedCounts: (moved: { clips: number; documents: number; files: number; calls: number }) =>
        (
            [
                [moved.clips, "clip"],
                [moved.documents, "document"],
                [moved.files, "file"],
                [moved.calls, "call"],
            ] as const
        )
            .filter(([n]) => n > 0)
            .map(([n, noun]) => `${n} ${noun}${n === 1 ? "" : "s"}`)
            .join(", "),
    unmergeTitle: (time: string) => `Unmerge the ${time} visit?`,
    unmergeSubtitle: (when: string) => `This puts both visits back the way they were before ${when}.`,
    unmergeExported: (docType: string, pims: string) =>
        `The ${docType} was exported to ${pims} after merging. Unmerging won't remove it from ${pims}.`,
    /** keptTime is empty when the confirm can't tell the kept visit's time. */
    unmergeTail: (keptTime: string) =>
        keptTime
            ? `Anything added after the merge stays on the ${keptTime} visit.`
            : "Anything added after the merge stays on the visit it was added to.",
    unmerge: "Unmerge",
    unmergedToast: (time: string) => `Unmerged. The ${time} visit is back.`,
    recycleMerged: "Merged",
    recycleInto: (label: string) => `into ${label}`,
    /** time is empty where only the visit's id is known. */
    recycleOpenKept: (time: string) => (time ? `Go to the ${time} visit` : "Go to the other visit"),
    /** A restore the API refused because the visit was merged. */
    recycleRestoreMerged: "Merged visits come back with Unmerge.",
    /** A restore the API refused because it could not check for a merge. */
    recycleRestoreCheckFailed: "Couldn't check whether this visit was merged. Please try again.",
    /** A restore of an item the API no longer finds in this clinic (restored already, or deleted). */
    recycleRestoreNotFound: "It's no longer in the Recycle Bin. It may have been restored already.",
} as const;

/** Reason codes the merge API can return. Derived from the copy deck so every code has a string. */
export type MergeBlockReason = keyof typeof COPY.reason;
export const MERGE_BLOCK_REASONS = Object.keys(COPY.reason) as MergeBlockReason[];

export const isMergeBlockReason = (value: unknown): value is MergeBlockReason =>
    typeof value === "string" && Object.prototype.hasOwnProperty.call(COPY.reason, value);

/** As reasonText, but in unmerge words where the shared reason reads as a merge. */
export function unmergeReasonText(reason: MergeBlockReason | null, pimsName: string): string {
    return reason === "visit_changed" ? COPY.unmergeChanged : reasonText(reason, pimsName);
}

/** null, or a reason this build does not know, reads as the generic fallback. */
export function reasonText(reason: MergeBlockReason | null, pimsName: string): string {
    if (!isMergeBlockReason(reason)) return COPY.reasonFallback;
    return reason === "different_pims_patient"
        ? COPY.reason.different_pims_patient(pimsName)
        : (COPY.reason[reason] as string);
}
